import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { PageContent } from '../components/layout/PageContent';
import { CabecalhoPagina, FaixaFiltros } from '../components/layout/CabecalhoPagina';
import { useAuth } from '../contexts/AuthContext';
import { registrationApi, championshipApi, athleteApi, teamApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ClipboardList, Plus, Search, Loader2, CheckCircle, Clock, Trash2, Trophy, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { EstadoVazio } from '../components/common/Estados';

export default function RegistrationsPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isProfessor = user?.role === 'professor';
  const isAthlete = user?.role === 'athlete';
  const canManage = ['super_admin', 'federation', 'professor', 'team'].includes(user?.role);
  const [registrations, setRegistrations] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const params = useParams();
  const contextId = params.id || searchParams.get('championship_id') || '';
  const [selectedChampionship, setSelectedChampionship] = useState(contextId);
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalityFilter, setModalityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [athleteSearch, setAthleteSearch] = useState('');

  const [formData, setFormData] = useState({
    championship_id: searchParams.get('championship_id') || '',
    athlete_id: '',
    weight_category: '',
    is_absolute: false,
    modality: 'gi'  // gi (Kimono) | nogi (No-Gi) — usado no modo misto
  });

  const fetchData = async () => {
    try {
      const [champsRes, athletesRes, teamsRes] = await Promise.all([
        championshipApi.list(),
        athleteApi.list(),
        teamApi.list(),
      ]);
      setChampionships(champsRes.data);
      setAthletes(athletesRes.data);
      setTeams(teamsRes.data || []);

      if (selectedChampionship) {
        const regsRes = await registrationApi.list({ championship_id: selectedChampionship });
        setRegistrations(regsRes.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedChampionship]);

  const handleConfirm = async (id) => {
    try {
      await registrationApi.confirm(id);
      toast.success('Inscricao confirmada!');
      fetchData();
    } catch (error) { toast.error('Erro ao confirmar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta inscricao?')) return;
    try {
      await registrationApi.delete(id);
      toast.success('Inscricao excluida');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await registrationApi.create(formData);
      toast.success('Inscricao realizada!');
      setIsDialogOpen(false);
      setFormData({ ...formData, athlete_id: '', weight_category: '' });
      setAthleteSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao inscrever');
    } finally { setSaving(false); }
  };

  const handleOpenDialog = () => {
    // Keep the selected championship when opening the dialog
    setFormData(prev => ({
      ...prev,
      championship_id: selectedChampionship || prev.championship_id,
      athlete_id: '',
      weight_category: '',
      modality: 'gi'
    }));
    setAthleteSearch('');
    setIsDialogOpen(true);
  };

  const selectAthlete = (athlete) => {
    setFormData(prev => ({
      ...prev,
      athlete_id: athlete._id,
      weight_category: athlete.age_class || ''
    }));
    setAthleteSearch(athlete.name);
  };

  const filteredAthletes = athletes.filter(a =>
    athleteSearch.length > 0 && a.name.toLowerCase().includes(athleteSearch.toLowerCase())
  );

  const selectedAthlete = athletes.find(a => a._id === formData.athlete_id);

  // Modalidade do campeonato escolhido no dialogo (gi | nogi | misto)
  const dialogChamp = championships.find(c => c._id === formData.championship_id);
  const dialogChampMisto = (dialogChamp?.modality || 'gi') === 'misto';

  const selectedChampionshipObj = championships.find(c => c._id === selectedChampionship);
  const isChampionshipOpen = selectedChampionshipObj?.status === 'open';
  // Wave 3 — Professor can only delete/modify registrations of their OWN students
  // (using professor_id when present, falling back to team_id for legacy data)
  // AND only while the championship registrations are open.
  // Non-professor managers (admin/federation/team) are unrestricted.
  const profTeamId = user?.team_id;
  const profId = user?._id;
  // Build the full set of team ids the professor manages (new hierarchy)
  const profTeamIds = useMemo(() => {
    if (!isProfessor || !profId) return new Set();
    const ids = new Set();
    teams.forEach(t => {
      if (t.president_id === profId) ids.add(t._id);
      if (Array.isArray(t.professor_ids) && t.professor_ids.includes(profId)) ids.add(t._id);
    });
    if (profTeamId) ids.add(profTeamId);
    return ids;
  }, [isProfessor, profId, profTeamId, teams]);
  const canModifyRegistration = (reg) => {
    if (!isProfessor) return true;
    if (!isChampionshipOpen) return false;
    const owns = reg.professor_id
      ? reg.professor_id === profId
      : !!(reg.team_id && profTeamIds.has(reg.team_id));
    return owns;
  };

  const filteredRegistrations = registrations.filter(r => {
    const matchesSearch = r.athlete_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.payment_status === statusFilter;
    const matchesModality =
      modalityFilter === 'all' ||
      (modalityFilter === 'absolute' && r.is_absolute === true) ||
      (modalityFilter === 'weight' && !r.is_absolute);
    return matchesSearch && matchesStatus && matchesModality;
  });

  // ---------- Client-side pagination ----------
  const totalPages = Math.max(1, Math.ceil(filteredRegistrations.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = filteredRegistrations.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, filteredRegistrations.length);
  const pagedRegistrations = filteredRegistrations.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const renderPaginationBar = (showPageSize) => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-3">
      <div className="flex flex-wrap items-center gap-3 text-sm text-[#A1A1AA]">
        {showPageSize && (
          <>
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Exibir por pagina</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger data-testid="page-size-select" className="bg-[#1A1A1A] border-[#27272A] text-white h-8 w-[76px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                {[25, 50, 75, 100].map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-white hover:bg-[#27272A]">{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        <span>Mostrando <span className="text-white font-semibold">{start}-{end}</span> de <span className="text-white font-semibold">{filteredRegistrations.length}</span> inscricoes</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="prev-page" aria-label="Página anterior" className="border-[#27272A] text-white">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="px-2 text-sm text-[#A1A1AA]">Pagina <span className="text-white font-semibold">{currentPage}</span> / {totalPages}</span>
        <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="next-page" aria-label="Próxima página" className="border-[#27272A] text-white">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <PageContent>
      <div className="space-y-6" data-testid="registrations-page">
        <CabecalhoPagina
          contexto="Competição"
          titulo="Inscricoes"
          descricao="Gerencie as inscricoes nos campeonatos"
          icone={ClipboardList}
          acoes={
            canManage && (
              <Button onClick={handleOpenDialog} data-testid="new-registration-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
                <Plus size={20} className="mr-2" /> Nova Inscricao
              </Button>
            )
          }
        />

        {/* Filtros em faixa própria (Etapa Visual 2) */}
        <FaixaFiltros rotulo="Buscar e filtrar">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="space-y-2 flex-1 max-w-xs">
            <Label className="text-[#A1A1AA]">Campeonato</Label>
            <Select value={selectedChampionship || "none"} onValueChange={(v) => { setSelectedChampionship(v === "none" ? "" : v); setPage(1); }}>
              <SelectTrigger data-testid="filter-championship" className="bg-[#141414] border-[#27272A] text-white"><SelectValue placeholder="Selecione um campeonato" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                {championships.map((ch) => (
                  <SelectItem key={ch._id} value={ch._id} className="text-white hover:bg-[#27272A]">{ch.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 flex-1 max-w-xs">
            <Label className="text-[#A1A1AA]">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={20} />
              <Input placeholder="Nome do atleta..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="search-registrations" className="pl-10 bg-[#141414] border-[#27272A] text-white" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]">Status</Label>
            <div className="flex gap-2">
              <Button variant={statusFilter === 'all' ? 'default' : 'outline'} onClick={() => { setStatusFilter('all'); setPage(1); }} size="sm" className={statusFilter === 'all' ? 'bg-[#D4AF37] text-black' : 'border-[#27272A] text-[#A1A1AA]'}>Todos</Button>
              <Button variant={statusFilter === 'pending' ? 'default' : 'outline'} onClick={() => { setStatusFilter('pending'); setPage(1); }} size="sm" className={statusFilter === 'pending' ? 'bg-[#D4AF37] text-black' : 'border-[#27272A] text-[#A1A1AA]'}><Clock size={16} className="mr-1" /> Pendente</Button>
              <Button variant={statusFilter === 'confirmed' ? 'default' : 'outline'} onClick={() => { setStatusFilter('confirmed'); setPage(1); }} size="sm" className={statusFilter === 'confirmed' ? 'bg-[#D4AF37] text-black' : 'border-[#27272A] text-[#A1A1AA]'}><CheckCircle size={16} className="mr-1" /> Confirmado</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA]">Modalidade</Label>
            <div className="flex gap-2">
              <Button
                data-testid="modality-filter-all"
                variant={modalityFilter === 'all' ? 'default' : 'outline'}
                onClick={() => { setModalityFilter('all'); setPage(1); }}
                size="sm"
                className={modalityFilter === 'all' ? 'bg-[#D4AF37] text-black' : 'border-[#27272A] text-[#A1A1AA]'}
              >
                Todos
              </Button>
              <Button
                data-testid="modality-filter-weight"
                variant={modalityFilter === 'weight' ? 'default' : 'outline'}
                onClick={() => { setModalityFilter('weight'); setPage(1); }}
                size="sm"
                className={modalityFilter === 'weight' ? 'bg-[#D4AF37] text-black' : 'border-[#27272A] text-[#A1A1AA]'}
              >
                Peso
              </Button>
              <Button
                data-testid="modality-filter-absolute"
                variant={modalityFilter === 'absolute' ? 'default' : 'outline'}
                onClick={() => { setModalityFilter('absolute'); setPage(1); }}
                size="sm"
                className={modalityFilter === 'absolute' ? 'bg-[#D4AF37] text-black' : 'border-[#27272A] text-[#A1A1AA]'}
              >
                <Trophy size={16} className="mr-1" /> Absoluto
              </Button>
            </div>
          </div>
        </div>
        </FaixaFiltros>

        {/* Content */}
        {!selectedChampionship ? (
          <Card className="bg-[#141414] border-[#27272A]"><CardContent>
              <EstadoVazio icone={Trophy} titulo="Selecione um campeonato" descricao="Escolha um campeonato para ver as inscricoes" />
            </CardContent></Card>
        ) : loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : filteredRegistrations.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]"><CardContent>
              <EstadoVazio icone={ClipboardList} titulo="Nenhuma inscricao encontrada" descricao="Adicione atletas ao campeonato" />
            </CardContent></Card>
        ) : (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardHeader><CardTitle className="text-white font-['Chivo']">Inscritos ({filteredRegistrations.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="border-b border-[#27272A]/60">{renderPaginationBar(true)}</div>
              <div className="overflow-x-auto">
                <table className="tabela w-full">
                  <thead><tr className="border-b border-[#27272A]">
                    <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Atleta</th>
                    <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Faixa</th>
                    <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Peso</th>
                    <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Categoria</th>
                    <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Status</th>
                    {canManage && <th className="text-right py-3 px-4 text-[#D4AF37] font-medium">Acoes</th>}
                  </tr></thead>
                  <tbody>
                    {pagedRegistrations.map((reg) => (
                      <tr key={reg._id} className="border-b border-[#27272A]/50 hover:bg-[#1A1A1A]">
                        <td className="py-3 px-4 text-white font-medium">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{reg.athlete_name}</span>
                            {reg.is_absolute ? (
                              <span
                                data-testid={`absolute-badge-${reg._id}`}
                                title="Inscrito na modalidade Absoluto"
                                className="badge-modality badge-modality--absolute"
                              >
                                <Trophy size={12} /> Absoluto
                              </span>
                            ) : (
                              <span
                                data-testid={`weight-badge-${reg._id}`}
                                title="Inscrito por categoria de peso"
                                className="badge-modality badge-modality--weight"
                              >
                                Peso
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-xs capitalize belt-${reg.athlete_belt}`}>{reg.athlete_belt}</span></td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{reg.athlete_weight} kg</td>
                        <td className="py-3 px-4 text-[#A1A1AA]">{reg.athlete_age_class}</td>
                        <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-xs status-${reg.payment_status}`}>{reg.payment_status === 'confirmed' ? 'Confirmado' : 'Pendente'}</span></td>
                        <td className="py-3 px-4 text-right">
                          {canManage && (
                            <div className="flex items-center justify-end gap-2">
                              {reg.payment_status !== 'confirmed' && (
                                <Button variant="outline" size="sm" onClick={() => handleConfirm(reg._id)} className="border-green-500/50 text-green-500 hover:bg-green-500/10"><CheckCircle size={16} className="mr-1" /> Confirmar</Button>
                              )}
                              {canModifyRegistration(reg) ? (
                                <Button
                                  data-testid={`delete-registration-${reg._id}`}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(reg._id)}
                                  className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              ) : (
                                <Button
                                  data-testid={`delete-registration-disabled-${reg._id}`}
                                  variant="outline"
                                  size="sm"
                                  disabled
                                  title={
                                    isProfessor && (reg.professor_id ? reg.professor_id !== profId : reg.team_id !== profTeamId)
                                      ? "Voce so pode excluir inscricoes dos seus proprios alunos"
                                      : "Exclusao bloqueada: as inscricoes deste campeonato nao estao mais abertas"
                                  }
                                  className="border-[#27272A] text-[#52525B] cursor-not-allowed"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-[#27272A]/60">{renderPaginationBar(false)}</div>
            </CardContent>
          </Card>
        )}

        {/* New Registration Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-[#141414] border-[#27272A] max-w-lg">
            <DialogHeader><DialogTitle className="text-white font-['Chivo']">Nova Inscricao</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Campeonato *</Label>
                <Select value={formData.championship_id || "none"} onValueChange={(v) => {
                  const c = championships.find(ch => ch._id === v);
                  // Evento de modalidade unica: fixa a modalidade correta automaticamente
                  const forced = c?.modality === 'nogi' ? 'nogi' : c?.modality === 'misto' ? formData.modality || 'gi' : 'gi';
                  setFormData({ ...formData, championship_id: v === "none" ? "" : v, modality: forced });
                }}>
                  <SelectTrigger data-testid="reg-championship" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue placeholder="Selecione o campeonato" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                    {championships.map((ch) => (
                      <SelectItem key={ch._id} value={ch._id} className="text-white hover:bg-[#27272A]">{ch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Atleta *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={16} />
                  <Input
                    placeholder="Digite para buscar atleta..."
                    value={athleteSearch}
                    onChange={(e) => { setAthleteSearch(e.target.value); if (formData.athlete_id) setFormData(prev => ({ ...prev, athlete_id: '' })); }}
                    data-testid="reg-athlete-search"
                    className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white"
                  />
                </div>
                {/* Selected athlete display */}
                {selectedAthlete && (
                  <div className="p-2 rounded bg-green-900/20 border border-green-800/30 text-sm">
                    <span className="text-green-400 font-medium">{selectedAthlete.name}</span>
                    <span className="text-[#A1A1AA] ml-2">— {selectedAthlete.age_class || 'Adulto'}, {selectedAthlete.weight}kg, {selectedAthlete.belt}</span>
                  </div>
                )}
                {/* Dropdown list */}
                {!formData.athlete_id && athleteSearch.length > 0 && (
                  <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] max-h-48 overflow-y-auto">
                    {filteredAthletes.length === 0 ? (
                      <p className="text-[#71717A] text-sm text-center py-3">Nenhum atleta encontrado</p>
                    ) : (
                      filteredAthletes.slice(0, 20).map((a) => (
                        <button
                          key={a._id}
                          type="button"
                          onClick={() => selectAthlete(a)}
                          className="w-full text-left px-3 py-2 hover:bg-[#27272A] transition-colors"
                        >
                          <span className="text-white">{a.name}</span>
                          <span className="text-[#A1A1AA] text-sm ml-2">— {a.age_class || 'Adulto'}, {a.weight}kg, {a.belt}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {dialogChampMisto && (
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Modalidade *</Label>
                  <Select value={formData.modality || 'gi'} onValueChange={(v) => setFormData({ ...formData, modality: v })}>
                    <SelectTrigger data-testid="reg-modality" className="bg-[#1A1A1A] border-[#27272A] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="gi" className="text-white hover:bg-[#27272A]">Kimono</SelectItem>
                      <SelectItem value="nogi" className="text-white hover:bg-[#27272A]">No-Gi</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#71717A]">No evento misto o mesmo atleta pode entrar nas duas modalidades (inscrições separadas).</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#27272A] text-[#A1A1AA]">Cancelar</Button>
                <Button type="submit" disabled={saving || !formData.championship_id || !formData.athlete_id} data-testid="save-registration" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Inscrever'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </PageContent>
  );
}
